import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeCanvas } from 'qrcode.react';
import { shopAdminApi } from '../shop/shopAdminApi';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { customerAccountsApi, customerPaymentsApi, customersApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency, formatDate, num, today } from '../../utils/format';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSFilterBar } from '../../components/ILMSFilterBar';
import { ILMSKPI } from '../../components/ILMSKPI';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import type { Tone } from '../../utils/statusTone';
import type {
  AccountStatus, BillingMode, Customer, CustomerAccountSummary, CustomerPayment, CustomerPaymentMethod, UnpaidCycle,
} from '../../types';
import type { FieldConfig, ModuleConfig } from '../../features/common/types';

// Billing rules (Prepaid wallet / Postpaid billing cycle + due date) live
// in the backend's app/services/billing_service.py - every figure on this
// page is computed there fresh on each load, never stored.

export const PAYMENT_METHOD_OPTIONS: { value: CustomerPaymentMethod; label: string }[] = [
  { value: 'Cash', label: 'Cash' },
  { value: 'OfficeQR', label: 'Office QR (UPI)' },
  { value: 'WhatsAppQR', label: 'WhatsApp QR (UPI)' },
];
// WebsiteUPI isn't offered above - only the website records it, once staff verify the customer's UPI payment.
const METHOD_LABEL = { ...Object.fromEntries(PAYMENT_METHOD_OPTIONS.map((o) => [o.value, o.label])), WebsiteUPI: 'Website (UPI)' } as Record<CustomerPaymentMethod, string>;

const STATUS_META: Record<AccountStatus, { label: string; tone: Tone }> = {
  Ok: { label: 'OK', tone: 'success' },
  LowBalance: { label: 'Low Balance', tone: 'warning' },
  WalletEmpty: { label: 'Wallet Empty', tone: 'critical' },
  Due: { label: 'Due', tone: 'info' },
  Overdue: { label: 'Overdue', tone: 'critical' },
};

export function AccountStatusBadge({ status }: { status: AccountStatus }) {
  const meta = STATUS_META[status];
  return <ILMSStatusBadge status={meta.label} tone={meta.tone} size="sm" />;
}

const selectClass = 'h-11 min-h-touch rounded-md border border-ink-300/50 bg-white px-3 text-body focus-visible:outline-2 focus-visible:outline-brand-green-500';
const inputClass = `${selectClass} w-full`;

// ---------------------------------------------------------------------------
// Customer Accounts - the dues/wallet monitor
// ---------------------------------------------------------------------------

export function CustomerAccountsTab() {
  const { can } = useAuth();
  const [q, setQ] = React.useState('');
  const [mode, setMode] = React.useState<BillingMode | ''>('');
  const [status, setStatus] = React.useState<AccountStatus | ''>('');
  const [paying, setPaying] = React.useState<CustomerAccountSummary | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['customer-accounts', q, mode, status],
    queryFn: () => customerAccountsApi.list({ q: q || undefined, billing_mode: mode || undefined, status: status || undefined }),
  });
  // KPIs always reflect every customer, not just the filtered rows.
  const allQuery = useQuery({ queryKey: ['customer-accounts', 'all'], queryFn: () => customerAccountsApi.list() });

  const totals = React.useMemo(() => {
    const all = allQuery.data ?? [];
    const sum = (rows: CustomerAccountSummary[], pick: (r: CustomerAccountSummary) => unknown) => rows.reduce((t, r) => t + (num(pick(r) as string) ?? 0), 0);
    const overdue = all.filter((r) => r.status === 'Overdue');
    const due = all.filter((r) => r.billing_mode === 'Postpaid');
    const lowWallets = all.filter((r) => r.status === 'LowBalance' || r.status === 'WalletEmpty');
    const prepaid = all.filter((r) => r.billing_mode === 'Prepaid');
    return {
      overdueAmount: sum(overdue, (r) => r.overdue_amount), overdueCount: overdue.length,
      billedAmount: sum(due, (r) => r.billed_outstanding) - sum(overdue, (r) => r.overdue_amount),
      lowWalletCount: lowWallets.length,
      walletCredit: prepaid.reduce((t, r) => t + Math.max(0, num(r.wallet_balance) ?? 0), 0),
    };
  }, [allQuery.data]);

  const columns: ILMSTableColumn<CustomerAccountSummary>[] = [
    { key: 'customer', header: 'Customer', primary: true, render: (r) => (
      <span>{r.customer_name}<span className="ml-1.5 text-helper-text text-ink-400">{r.customer_code ?? ''}</span></span>
    ) },
    { key: 'mode', header: 'Billing', render: (r) => (r.billing_mode === 'Prepaid' ? 'Prepaid wallet' : `Postpaid · ${r.billing_cycle ?? ''}`) },
    { key: 'status', header: 'Status', render: (r) => <AccountStatusBadge status={r.status} /> },
    { key: 'balance', header: 'Wallet / Due', align: 'right', render: (r) => (
      r.billing_mode === 'Prepaid'
        ? <span className={(num(r.wallet_balance) ?? 0) <= 0 ? 'text-critical-700 font-medium' : ''}>{formatCurrency(r.wallet_balance)}</span>
        : formatCurrency(Math.max(0, num(r.balance_due) ?? 0))
    ) },
    { key: 'overdue', header: 'Overdue', align: 'right', render: (r) => (
      r.billing_mode === 'Postpaid' && (num(r.overdue_amount) ?? 0) > 0
        ? <span className="text-critical-700 font-medium">{formatCurrency(r.overdue_amount)} <span className="text-helper-text">({r.days_overdue}d)</span></span>
        : '—'
    ) },
    { key: 'next', header: 'Next Due / Days Left', render: (r) => (
      r.billing_mode === 'Prepaid'
        ? (r.days_remaining != null ? `${r.days_remaining} day${r.days_remaining === 1 ? '' : 's'} left` : '—')
        : formatDate(r.next_due_date)
    ) },
    { key: 'last_payment', header: 'Last Payment', hideOnTablet: true, render: (r) => (
      r.last_payment_date ? `${formatDate(r.last_payment_date)} · ${formatCurrency(r.last_payment_amount)}` : '—'
    ) },
    { key: 'delivered', header: 'Delivered', align: 'right', hideOnTablet: true, render: (r) => formatCurrency(r.total_delivered) },
    { key: 'paid', header: 'Paid', align: 'right', hideOnTablet: true, render: (r) => formatCurrency(r.total_paid) },
  ];

  const activeCount = (mode ? 1 : 0) + (status ? 1 : 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <ILMSKPI label="Overdue (Postpaid)" value={formatCurrency(totals.overdueAmount)} tone="critical"
          helperText={`${totals.overdueCount} customer${totals.overdueCount === 1 ? '' : 's'}`}
          onClick={() => { setMode(''); setStatus('Overdue'); }} />
        <ILMSKPI label="Billed, Not Yet Due" value={formatCurrency(Math.max(0, totals.billedAmount))} tone="info"
          onClick={() => { setMode(''); setStatus('Due'); }} />
        <ILMSKPI label="Low / Empty Wallets" value={totals.lowWalletCount} tone="warning"
          helperText="Prepaid customers to remind" onClick={() => { setMode('Prepaid'); setStatus(''); }} />
        <ILMSKPI label="Wallet Credit Held" value={formatCurrency(totals.walletCredit)} tone="success"
          helperText="Prepaid money not yet delivered against" />
      </div>

      <ILMSFilterBar
        searchValue={q} onSearchChange={setQ} searchPlaceholder="Search by customer name or code…"
        activeCount={activeCount} onClear={() => { setMode(''); setStatus(''); }}
      >
        <select aria-label="Billing mode" className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as BillingMode | '')}>
          <option value="">All billing modes</option>
          <option value="Prepaid">Prepaid wallet</option>
          <option value="Postpaid">Postpaid</option>
        </select>
        <select aria-label="Status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value as AccountStatus | '')}>
          <option value="">All statuses</option>
          {(Object.keys(STATUS_META) as AccountStatus[]).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
      </ILMSFilterBar>

      <ILMSTable
        columns={columns}
        data={accountsQuery.data ?? []}
        rowKey={(r) => r.customer_id}
        exportTitle="Customer Accounts"
        isLoading={accountsQuery.isLoading}
        error={accountsQuery.isError ? getApiErrorMessage(accountsQuery.error, 'Unable to load customer accounts.') : null}
        onRetry={() => accountsQuery.refetch()}
        onRowClick={can('Sales', 'Create') ? setPaying : undefined}
        emptyTitle="No customers match these filters."
      />
      {can('Sales', 'Create') && (
        <p className="text-helper-text text-ink-400">Click a customer to record a payment or wallet top-up.</p>
      )}

      {paying && <RecordPaymentModal account={paying} onClose={() => setPaying(null)} />}
    </div>
  );
}

const cycleLabel = (c: UnpaidCycle, cycle?: string | null) => {
  const d = new Date(`${c.start}T00:00:00`);
  if (cycle === 'Weekly') return `${formatDate(c.start, 'dd MMM yyyy')} – ${formatDate(c.end, 'dd MMM yyyy')}`;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

type PayChoice = { id: string; label: string; amount: number; hint?: string };

// The quick amounts offered when recording a payment: the whole due, only
// what is overdue / already billed, or (Prepaid) a top-up for so many days.
function payChoices(a: CustomerAccountSummary): PayChoice[] {
  const out: PayChoice[] = [];
  if (a.billing_mode === 'Postpaid') {
    const all = Math.max(0, num(a.balance_due) ?? 0);
    const billed = Math.max(0, num(a.billed_outstanding) ?? 0);
    const overdue = Math.max(0, num(a.overdue_amount) ?? 0);
    if (all > 0) out.push({ id: 'all', label: 'Overall due', amount: all, hint: billed < all ? 'includes this month so far' : undefined });
    if (billed > 0 && billed < all) out.push({ id: 'billed', label: 'Billed months only', amount: billed, hint: 'not the running month' });
    if (overdue > 0 && overdue < billed) out.push({ id: 'overdue', label: 'Overdue only', amount: overdue });
    const oldest = (a.unpaid_cycles ?? [])[0];
    if (oldest && num(oldest.unpaid)! < all) out.push({ id: 'oldest', label: `Oldest bill (${cycleLabel(oldest, a.billing_cycle)})`, amount: num(oldest.unpaid) ?? 0, hint: 'minimum to clear the oldest' });
  } else {
    const daily = num(a.daily_value) ?? 0;
    const wallet = num(a.wallet_balance) ?? 0;
    if (wallet < 0) out.push({ id: 'clear', label: 'Clear the minus balance', amount: -wallet });
    if (daily > 0) {
      for (const days of [7, 15, 30]) out.push({ id: `d${days}`, label: `${days} days of milk`, amount: daily * days });
      const fill = daily * 30 - wallet;
      if (fill > 0 && wallet > 0) out.push({ id: 'fill', label: 'Fill up to 30 days', amount: fill, hint: `wallet has ${formatCurrency(wallet)}` });
    }
  }
  return out.filter((c) => c.amount > 0);
}

function RecordPaymentModal({ account, onClose }: { account: CustomerAccountSummary; onClose: () => void }) {
  const queryClient = useQueryClient();
  const choices = React.useMemo(() => payChoices(account), [account]);
  const cycles = account.unpaid_cycles ?? [];
  // 'months' = pick bills; 'other' = type any amount; else one of the choices.
  const [mode, setMode] = React.useState<string>(choices[0]?.id ?? 'other');
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set(cycles.filter((c) => !c.is_open).map((c) => c.start)));
  const [amount, setAmount] = React.useState(() => (choices[0] ? choices[0].amount.toFixed(2) : ''));
  const [method, setMethod] = React.useState<CustomerPaymentMethod>('Cash');
  const [paidAt, setPaidAt] = React.useState(today());
  const [notes, setNotes] = React.useState('');

  const pickChoice = (id: string) => {
    setMode(id);
    const c = choices.find((x) => x.id === id);
    if (c) setAmount(c.amount.toFixed(2));
    if (id === 'months') applyMonths(picked);
    if (id === 'other') setAmount('');
  };
  const applyMonths = (set: Set<string>) => {
    setPicked(set);
    setAmount(cycles.filter((c) => set.has(c.start)).reduce((t, c) => t + (num(c.unpaid) ?? 0), 0).toFixed(2));
  };
  const toggleMonth = (start: string) => {
    const next = new Set(picked);
    if (next.has(start)) next.delete(start); else next.add(start);
    applyMonths(next);
  };
  const monthsNote = mode === 'months' && picked.size
    ? `For ${cycles.filter((c) => picked.has(c.start)).map((c) => cycleLabel(c, account.billing_cycle)).join(', ')}` : '';

  const mutation = useMutation({
    mutationFn: () => customerPaymentsApi.create({
      customer_id: account.customer_id, amount: amount as unknown as CustomerPayment['amount'], method, paid_at: paidAt,
      notes: [notes.trim(), monthsNote].filter(Boolean).join(' · ') || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['customer-payments'] });
      onClose();
    },
  });
  const valid = Number(amount) > 0 && !!paidAt;

  return (
    <ILMSModal
      open onClose={onClose}
      title={account.billing_mode === 'Prepaid' ? `Wallet top-up — ${account.customer_name}` : `Record payment — ${account.customer_name}`}
      footer={(
        <>
          <ILMSButton variant="ghost" onClick={onClose}>Cancel</ILMSButton>
          <ILMSButton disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>Save Payment</ILMSButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="text-secondary text-ink-500">
          {account.billing_mode === 'Prepaid'
            ? `Wallet balance ${formatCurrency(account.wallet_balance)}${account.days_remaining != null ? ` (about ${account.days_remaining} days of deliveries)` : ''}.`
            : `Outstanding ${formatCurrency(Math.max(0, num(account.balance_due) ?? 0))}${(num(account.overdue_amount) ?? 0) > 0 ? `, of which ${formatCurrency(account.overdue_amount)} is overdue` : ''}.`}
        </p>
        <div className="flex flex-col gap-1.5">
          <span className="text-label text-ink-700">What is being paid?</span>
          <div className="flex flex-wrap gap-1.5">
            {[...choices, ...(cycles.length > 1 ? [{ id: 'months', label: 'Pick months', amount: 0 }] : []), { id: 'other', label: 'Other amount', amount: 0 }].map((c) => (
              <button key={c.id} type="button" onClick={() => pickChoice(c.id)}
                className={`rounded-full border px-3 py-1.5 text-secondary font-medium ${mode === c.id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700 hover:border-brand-green-400'}`}>
                {c.label}{c.amount > 0 ? ` · ${formatCurrency(c.amount)}` : ''}
              </button>
            ))}
          </div>
          {choices.find((c) => c.id === mode)?.hint && <p className="text-helper-text text-ink-500">{choices.find((c) => c.id === mode)!.hint}</p>}
        </div>
        {mode === 'months' && (
          <div className="flex flex-col divide-y divide-ink-300/20 rounded-md border border-ink-300/30">
            {cycles.map((c) => (
              <label key={c.start} className="flex items-center gap-2.5 px-3 py-2 text-secondary">
                <input type="checkbox" checked={picked.has(c.start)} onChange={() => toggleMonth(c.start)} />
                <span className="flex-1">
                  <span className="font-medium">{cycleLabel(c, account.billing_cycle)}</span>
                  <span className={`ml-2 text-helper-text ${c.is_overdue ? 'text-critical-700' : 'text-ink-500'}`}>
                    {c.is_open ? 'running, not billed yet' : c.is_overdue ? `overdue since ${formatDate(c.due_date)}` : `due ${formatDate(c.due_date)}`}
                    {num(c.unpaid) !== num(c.billed) ? ` · part paid (bill ${formatCurrency(c.billed)})` : ''}
                  </span>
                </span>
                <span className="tabular-nums">{formatCurrency(c.unpaid)}</span>
              </label>
            ))}
          </div>
        )}
        <label className="flex flex-col gap-1 text-label text-ink-700" htmlFor="payment-amount">
          Amount (₹)
          <input id="payment-amount" type="number" min={0} step="0.01" className={inputClass} value={amount}
            onChange={(e) => { setAmount(e.target.value); if (mode !== 'months') setMode('other'); }} placeholder="Type the amount received" />
          {mode === 'other' && account.billing_mode === 'Postpaid' && Number(amount) > 0 && (
            <span className="text-helper-text font-normal text-ink-500">
              A part payment clears the oldest bills first. {Number(amount) < Math.max(0, num(account.balance_due) ?? 0) ? `Still due after this: ${formatCurrency(Math.max(0, (num(account.balance_due) ?? 0) - Number(amount)))}.` : ''}
            </span>
          )}
        </label>
        <label className="flex flex-col gap-1 text-label text-ink-700" htmlFor="payment-method">
          Method
          <select id="payment-method" className={inputClass} value={method} onChange={(e) => setMethod(e.target.value as CustomerPaymentMethod)}>
            {PAYMENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {(method === 'OfficeQR' || method === 'WhatsAppQR') && <PaymentQr account={account} amount={amount} whatsapp={method === 'WhatsAppQR'} />}
        <label className="flex flex-col gap-1 text-label text-ink-700" htmlFor="payment-date">
          Paid On
          <input id="payment-date" type="date" max={today()} className={inputClass} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-label text-ink-700" htmlFor="payment-notes">
          Notes (optional)
          <input id="payment-notes" className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. UPI ref no." />
        </label>
        {mutation.isError && <ILMSAlert tone="critical">{getApiErrorMessage(mutation.error, 'Unable to record this payment.')}</ILMSAlert>}
      </div>
    </ILMSModal>
  );
}

// The farm's UPI QR (the UPI ID saved under Online Shop -> Settings) with
// this payment's amount in it, for the customer to scan - at the counter, or
// saved as a picture and sent on WhatsApp.
function PaymentQr({ account, amount, whatsapp }: { account: CustomerAccountSummary; amount: string; whatsapp: boolean }) {
  const settings = useQuery({ queryKey: ['shop-admin', 'settings'], queryFn: shopAdminApi.settings, staleTime: 60_000 });
  const box = React.useRef<HTMLDivElement>(null);
  const s = settings.data;
  if (settings.isLoading) return <p className="text-helper-text text-ink-400">Loading the UPI QR…</p>;
  if (settings.isError) return <ILMSAlert tone="warning">{getApiErrorMessage(settings.error, 'Could not load the UPI ID.')}</ILMSAlert>;
  if (!s?.upi_id) {
    return <ILMSAlert tone="warning">No UPI ID saved yet. Add it under Online Shop → Settings and the payment QR shows here.</ILMSAlert>;
  }
  const amt = Number(amount) > 0 ? Number(amount).toFixed(2) : null;
  const ref = `${account.customer_code ?? account.customer_name} milk bill`;
  const link = `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.payee_name)}${amt ? `&am=${amt}` : ''}&cu=INR&tn=${encodeURIComponent(ref)}`;

  const saveImage = () => {
    const src = box.current?.querySelector('canvas');
    if (!src) return;
    const out = document.createElement('canvas');
    out.width = src.width + 80;
    out.height = src.height + 190;
    const g = out.getContext('2d')!;
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, out.width, out.height);
    g.drawImage(src, 40, 40);
    g.fillStyle = '#1b361f';
    g.textAlign = 'center';
    g.font = 'bold 44px sans-serif';
    g.fillText(amt ? formatCurrency(amt) : s.payee_name, out.width / 2, src.height + 100);
    g.font = '26px sans-serif';
    g.fillText(`${s.payee_name} · ${ref}`, out.width / 2, src.height + 140);
    const a = document.createElement('a');
    a.href = out.toDataURL('image/png');
    a.download = `Pay-${(account.customer_code ?? account.customer_name).replace(/\W+/g, '-')}.png`;
    a.click();
  };
  const message = `Namaste ${account.customer_name}, please pay ${amt ? formatCurrency(amt) : 'your milk bill'} to Sri KalpaGavya by UPI: ${s.upi_id} (${s.payee_name}). The QR is attached. Thank you.`;

  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-ink-300/30 bg-surface-subtle p-3 text-center">
      <div ref={box} className="rounded-md bg-white p-2">
        <QRCodeCanvas value={link} size={512} style={{ width: 180, height: 180 }} />
      </div>
      <p className="text-secondary">
        Pay <b>{amt ? formatCurrency(amt) : '(enter the amount)'}</b> to <b>{s.payee_name}</b> · {s.upi_id}
      </p>
      <p className="text-helper-text text-ink-500">
        {whatsapp ? 'Save the QR and send it on WhatsApp.' : 'Ask the customer to scan this with any UPI app.'} When the money shows in your UPI app, type its UPI ref in Notes and save.
      </p>
      {whatsapp && (
        <div className="flex flex-wrap justify-center gap-2">
          <ILMSButton size="sm" variant="outline" onClick={saveImage}>Save QR image</ILMSButton>
          {account.mobile && (
            <a href={`https://wa.me/91${account.mobile.replace(/\D/g, '').slice(-10)}?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center rounded-md bg-success-600 px-3 py-1.5 text-secondary font-semibold text-white">Open WhatsApp chat</a>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer Payments - the full payment history (edit/delete corrections)
// ---------------------------------------------------------------------------

const isCreating = (v: Record<string, unknown>) => v.id === undefined;

function buildPaymentsConfig(customersById: Map<string, Customer>): ModuleConfig<CustomerPayment> {
  return {
    title: 'Customer Payments', entityLabel: 'payment', module: 'Sales', queryKey: 'customer-payments', idKey: 'id',
    subtitle: 'Every payment and Prepaid wallet top-up received — feeds the Customer Accounts balances.',
    api: customerPaymentsApi, canDelete: true, referenceKeys: ['customers'], addLabel: 'Record Payment',
    searchPlaceholder: 'Search by customer name or code…',
    defaultValues: { method: 'Cash', paid_at: today() } as Partial<CustomerPayment>,
    columns: [
      { key: 'date', header: 'Paid On', primary: true, render: (r) => formatDate(r.paid_at) },
      { key: 'customer', header: 'Customer', render: (r) => customersById.get(r.customer_id)?.name ?? '—' },
      { key: 'mode', header: 'Billing', hideOnTablet: true, render: (r) => customersById.get(r.customer_id)?.billing_mode ?? '—' },
      { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount) },
      { key: 'method', header: 'Method', render: (r) => METHOD_LABEL[r.method] ?? r.method },
      { key: 'notes', header: 'Notes', hideOnTablet: true, render: (r) => r.notes || '—' },
    ],
    fields: [
      { name: 'customer_id', label: 'Customer / Partner / Vendor', type: 'select', optionsKey: 'customers', section: 'Payment',
        required: isCreating, visibleWhen: isCreating },
      { name: 'amount', label: 'Amount (₹)', type: 'decimal', section: 'Payment', required: true, min: 0 },
      { name: 'method', label: 'Method', type: 'select', section: 'Payment', required: true, staticOptions: PAYMENT_METHOD_OPTIONS },
      { name: 'paid_at', label: 'Paid On', type: 'date', section: 'Payment', required: true },
      { name: 'notes', label: 'Notes', type: 'text', section: 'Payment', optional: true, placeholder: 'e.g. UPI ref no.' },
    ] as FieldConfig<any>[],
  };
}

export function CustomerPaymentsTab() {
  const customersQuery = useQuery({ queryKey: ['customers-full-for-payments'], queryFn: () => customersApi.list({ page_size: 500 }) });
  const customersById = React.useMemo(() => {
    const map = new Map<string, Customer>();
    (customersQuery.data?.items ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [customersQuery.data]);
  return <ResourceModule config={buildPaymentsConfig(customersById)} />;
}

// For Administration -> Entry Form Settings.
registerForms('Online Shop & Customers', buildPaymentsConfig(new Map()));
